import React, { useContext, useEffect, useState } from "react";
import NavContainer from "../../NavContainer";
import { axiosInstance } from "../../config";
import CartContext from "../../CartContext";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { purchaseEvent } from "../../helpers/FacebookPixel";
import WILAYAS from "../../wilayas";
import LoadingIcon from "../../components/Icons/LoadingIcon";
import Loading from "../Loading";
import { useDebounce } from "use-debounce";
import SetsProductCardComponent from "./SetsProductCardComponent";

const SetLanding = () => {
  const { setId } = useParams();
  const [selectionWarning, setSelectionWarning] = useState(false);
  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    addressDetails: "",
    wilaya: "",
  });
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    wilaya: "",
    addressDetails: "",
  });
  const { orderPlaced } = useContext(CartContext);
  const [deliveryCharge, setDeliveryCharge] = useState(400);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [codePromoValue] = useDebounce(promoCode, 1000);
  const [codePromoError, setCodePromoError] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [totalItemPrice, setTotalItemPrice] = useState(0);
  const [firstProductInitialized, setFirstProductInitialized] = useState(false);

  useEffect(() => {
    const getSet = async () => {
      try {
        setLoading(true);
        const data = await axiosInstance.get(`/api/sets/${setId}`);
        setProducts(data.data.data.products);
        setSelectedProducts(data.data.data.products);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getSet();
  }, []);

  useEffect(() => {
    let total = 0;
    selectedProducts.forEach((product) => {
      total += product.price * (product.purchasedQuantity || 1);
    });
    const discounted =
      discountPercentage !== 0
        ? total - (total * discountPercentage) / 100
        : total;
    setTotalItemPrice(discounted);
  }, [selectedProducts, discountPercentage]);

  useEffect(() => {
    if (promoCode !== "") {
      axiosInstance
        .get(`/api/promoCodes/check/${codePromoValue}`)
        .then((res) => {
          if (res.data.data.isValid) {
            setDiscountPercentage(res.data.data.discountPercentage);
            setCodePromoError("");
          } else {
            setCodePromoError("الرمز غير صالح");
            setDiscountPercentage(0);
          }
        })
        .catch(() => {
          setCodePromoError("الرمز غير صالح");
          setDiscountPercentage(0);
        });
    } else {
      setCodePromoError("");
      setDiscountPercentage(0);
    }
  }, [codePromoValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({
      firstName: "",
      lastName: "",
      phone: "",
      wilaya: "",
      addressDetails: "",
    });
    let hasError = false;
    const requiredFields = [
      "firstName",
      "lastName",
      "phone",
      "wilaya",
      "addressDetails",
    ];
    requiredFields.forEach((field) => {
      if (!data[field]?.trim()) {
        setFieldErrors((prev) => ({ ...prev, [field]: "هذا الحقل مطلوب" }));
        hasError = true;
      }
    });
    if (hasError) return setError("الرجاء إكمال جميع الحقول المطلوبة");

    setSubmitting(true);
    try {
      await axiosInstance.post("/api/orders/", {
        clientFirstName: data.firstName,
        clientLastName: data.lastName,
        clientPhoneNumber: data.phone,
        products: selectedProducts.map((item) => ({
          id: item._id,
          quantity: item.purchasedQuantity || 1,
        })),
        addressDetails: data.addressDetails,
        wilaya: data.wilaya,
        origin: "web",
      });

      Swal.fire({
        position: "top",
        icon: "success",
        title: "تم تأكيد الطلبية",
        text: "سنتواصل معك قريبا",
        showConfirmButton: false,
        timer: 1250,
      });
      purchaseEvent(selectedProducts, totalItemPrice + deliveryCharge);
      orderPlaced();
      navigate("/", { replace: true });
    } catch (error) {
      setError("حدث خطأ ما، جرب مرة أخرى");
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  const deliveryPrice = (wilaya) => {
    const found = WILAYAS.find((w) => w.name === wilaya);
    return found ? found.price : 400;
  };

  useEffect(() => {
    setSelectionWarning(selectedProducts.length === 0);
  }, [selectedProducts]);

  const handleWilayaChange = (e) => {
    const wilaya = e.target.value;
    setData((prev) => ({ ...prev, wilaya }));
    setDeliveryCharge(deliveryPrice(wilaya));
  };

  const calculateItemsPrice = () => {
    let total = 0;
    selectedProducts.forEach((product) => {
      total += product.price * (product.purchasedQuantity || 1);
    });
    if (discountPercentage !== 0) {
      setTotalItemPrice(total - (total * discountPercentage) / 100);
    } else {
      setTotalItemPrice(total);
    }
  };

  return (
    <NavContainer>
      <div className="bg-blue-50 w-full py-3 text-center text-blue-700 text-lg font-medium">
        قم بتحديد الكتب التي ترغب في اقتنائها{" "}
      </div>

      {selectionWarning && (
        <div className="text-center text-red-700 bg-red-100 border border-red-300 font-bold text-lg py-2 my-2 rounded-xl animate-bounce mx-2">
          يجب اختيار منتج واحد على الأقل
        </div>
      )}
      {loading ? (
        <Loading />
      ) : (
        <div className="relative overflow-x-auto sm:overflow-visible">
          <div className="flex gap-4 px-4 py-6 w-max sm:w-full">
            {products.map((product, index) => (
              <SetsProductCardComponent
                key={product._id}
                product={product}
                index={index}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                calculateItemsPrice={calculateItemsPrice}
                firstProductInitialized={firstProductInitialized}
                setFirstProductInitialized={setFirstProductInitialized}
                selectionWarning={selectionWarning}
              />
            ))}
          </div>

          {/* Scroll hint for small screens */}
          <p className="text-center text-sm text-green-900 mt-1 mb-4 sm:hidden animate-pulse">
            ← اسحب لرؤية باقي الكتب →
          </p>
        </div>
      )}

      <div className="max-w-md mx-auto p-6 bg-white shadow-lg rounded-2xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="text-center text-xl font-bold">
            لطلب المنتج يرجى تعبئة النموذج
          </h2>

          {error && (
            <div className="text-red-600 bg-red-100 p-3 rounded-lg">
              {error}
            </div>
          )}

          {[
            { label: "الإسم الأول", name: "firstName" },
            { label: "اللقب", name: "lastName" },
            { label: "رقم الهاتف", name: "phone" },
            { label: "الدائرة", name: "addressDetails" },
          ].map(({ label, name }) => (
            <div key={name}>
              <label className="block text-sm font-medium">{label}</label>
              <input
                type="text"
                className={`w-full px-3 py-2 border rounded-xl ${
                  fieldErrors[name] ? "border-red-500" : ""
                }`}
                value={data[name]}
                onChange={(e) =>
                  setData((prev) => ({ ...prev, [name]: e.target.value }))
                }
              />
              {fieldErrors[name] && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors[name]}</p>
              )}
            </div>
          ))}

          <div>
            <label className="block text-sm font-medium">الولاية</label>
            <select
              className={`w-full px-3 py-2 border rounded-xl ${
                fieldErrors.wilaya ? "border-red-500" : ""
              }`}
              value={data.wilaya}
              onChange={handleWilayaChange}
            >
              <option value="">اختر الولاية</option>
              {WILAYAS.map((w) => (
                <option
                  key={w.id}
                  value={w.name}
                >{`${w.id} - ${w.name}`}</option>
              ))}
            </select>
            {fieldErrors.wilaya && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.wilaya}</p>
            )}
          </div>

          <div className="bg-blue-50 p-4 rounded-xl text-blue-900">
            <p>
              السعر الإجمالي للمنتجات: <strong>{totalItemPrice} دج</strong>{" "}
              {!!discountPercentage && (
                <span className="text-green-600">
                  ({discountPercentage}% خصم)
                </span>
              )}
            </p>
            <p>
              سعر التوصيل: <strong>{deliveryCharge} دج</strong>
            </p>
            <p className="text-lg font-bold mt-2">
              المجموع الكلي: <span>{totalItemPrice + deliveryCharge} دج</span>
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting || selectedProducts.length === 0}
            className="w-full bg-green-500 text-white font-bold py-2 rounded-xl hover:bg-green-600 transition duration-200 flex items-center justify-center gap-2"
          >
            {submitting && <LoadingIcon />} تأكيد الطلب
          </button>
        </form>
      </div>
    </NavContainer>
  );
};

export default SetLanding;
